import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  TrendingUp,
  TrendingDown,
  Wallet,
  ArrowUpRight,
  ArrowDownRight,
  AlertTriangle,
  Loader2,
  Calendar,
  DollarSign,
  CreditCard,
  PiggyBank,
} from 'lucide-react';
import { api } from '../../services/api';
import { useNotification } from '../../contexts/NotificationContext';
import { PageHeader } from '../../components/ui';
import { StatCard, Panel, Card, EmptyState, buttonClass } from '../../design-system';
import { ArrowRight } from 'lucide-react';
import { formatCurrency, formatDate } from '../../utils/format';

interface ContaBancaria {
  id: number;
  nome: string;
  tipo: string;
  saldoAtual: number;
  cor: string;
  principal: boolean;
}

interface LancamentoVencer {
  id: number;
  tipo: string;
  descricao: string;
  valor: number;
  dataVencimento: string;
  pessoaNome: string | null;
  categoriaNome: string | null;
  categoriaCor: string | null;
}

// DTO consolidado do dashboard otimizado
interface FinanceiroDashboard {
  saldoTotal: number;
  receitasRealizadas: number;
  despesasRealizadas: number;
  receitasPrevistas: number;
  despesasPrevistas: number;
  receitasVencidas: number;
  despesasVencidas: number;
  resultadoMes: number;
  contas: ContaBancaria[];
  contasVencer: LancamentoVencer[];
}

const atalhos = [
  { to: '/financeiro/contas-pagar', icon: ArrowDownRight, titulo: 'Contas a Pagar', desc: 'Despesas pendentes' },
  { to: '/financeiro/contas-receber', icon: ArrowUpRight, titulo: 'Contas a Receber', desc: 'Receitas pendentes' },
  { to: '/financeiro/fluxo-caixa', icon: TrendingUp, titulo: 'Fluxo de Caixa', desc: 'Projeção financeira' },
  { to: '/financeiro/contas-bancarias', icon: Wallet, titulo: 'Contas Bancárias', desc: 'Gerenciar contas' },
];

export function Financeiro() {
  const { showError } = useNotification();

  const [isLoading, setIsLoading] = useState(true);
  const [dashboard, setDashboard] = useState<FinanceiroDashboard | null>(null);

  const fetchData = useCallback(async (signal?: AbortSignal) => {
    setIsLoading(true);
    try {
      // Uma unica chamada de API consolidada (otimizado)
      const response = await api.get<FinanceiroDashboard>('/financeiro/dashboard', { signal });
      setDashboard(response.data);
    } catch (error) {
      if (error instanceof Error && error.name === 'CanceledError') return;
      showError('Erro ao carregar dados financeiros');
    } finally {
      setIsLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    const controller = new AbortController();
    fetchData(controller.signal);
    return () => controller.abort();
  }, [fetchData]);

  if (isLoading || !dashboard) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  const { saldoTotal, receitasRealizadas, despesasRealizadas, receitasPrevistas, despesasPrevistas, receitasVencidas, despesasVencidas, resultadoMes, contas, contasVencer } = dashboard;

  return (
    <div className="space-y-6">
      <PageHeader title="Financeiro" subtitle="Gestão financeira da empresa">
        <Link to="/financeiro/lancamentos/novo?tipo=DESPESA" className={buttonClass('secondary')}>
          <ArrowDownRight className="w-4 h-4 text-rose-500" />
          Nova Despesa
        </Link>
        <Link to="/financeiro/lancamentos/novo?tipo=RECEITA" className={buttonClass('primary')}>
          <ArrowUpRight className="w-4 h-4" />
          Nova Receita
        </Link>
      </PageHeader>

      {/* Cards de Resumo */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          title="Saldo Total"
          value={formatCurrency(saldoTotal)}
          icon={<Wallet className="h-[18px] w-[18px]" />}
          tone={saldoTotal >= 0 ? 'positive' : 'negative'}
          delay={0}
        />
        <StatCard
          title="Receitas do Mês"
          value={formatCurrency(receitasRealizadas)}
          subtitle={`Previsto: ${formatCurrency(receitasPrevistas)}`}
          icon={<TrendingUp className="h-[18px] w-[18px]" />}
          tone="positive"
          delay={60}
        />
        <StatCard
          title="Despesas do Mês"
          value={formatCurrency(despesasRealizadas)}
          subtitle={`Previsto: ${formatCurrency(despesasPrevistas)}`}
          icon={<TrendingDown className="h-[18px] w-[18px]" />}
          tone="negative"
          delay={120}
        />
        <StatCard
          title="Resultado do Mês"
          value={formatCurrency(resultadoMes)}
          icon={<DollarSign className="h-[18px] w-[18px]" />}
          tone={resultadoMes >= 0 ? 'positive' : 'negative'}
          delay={180}
        />
      </div>

      {/* Acesso Rápido */}
      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        {atalhos.map(({ to, icon: Icon, titulo, desc }) => (
          <Link key={to} to={to} className="group">
            <Card hoverable className="p-4">
              <div className="mb-3 grid h-10 w-10 place-items-center rounded-xl bg-slate-50 text-slate-400 ring-1 ring-slate-100 transition-colors group-hover:bg-primary-50 group-hover:text-primary-600 group-hover:ring-primary-100">
                <Icon className="h-5 w-5" />
              </div>
              <h4 className="font-bold text-slate-900">{titulo}</h4>
              <p className="text-sm text-slate-400">{desc}</p>
            </Card>
          </Link>
        ))}
      </div>

      {/* Alertas de Vencimento */}
      {(receitasVencidas > 0 || despesasVencidas > 0) && (
        <div className="flex items-center gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4">
          <div className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-amber-100 text-amber-600">
            <AlertTriangle className="w-5 h-5" />
          </div>
          <div>
            <p className="font-bold text-amber-800">Atenção! Você tem contas vencidas</p>
            <p className="text-sm text-amber-700">
              {receitasVencidas > 0 && <span>Receber: {formatCurrency(receitasVencidas)} </span>}
              {despesasVencidas > 0 && <span>Pagar: {formatCurrency(despesasVencidas)}</span>}
            </p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {/* Contas Bancárias */}
        <Panel
          title="Contas Bancárias"
          icon={<CreditCard className="h-4 w-4" />}
          action={
            <Link
              to="/financeiro/contas-bancarias"
              className="inline-flex items-center gap-1 text-xs font-bold text-primary-600 transition-colors hover:text-primary-700"
            >
              Ver todas <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          }
        >
          <div className="space-y-2">
            {contas.slice(0, 5).map((conta) => (
              <div key={conta.id} className="flex items-center justify-between rounded-xl bg-slate-50 p-3">
                <div className="flex items-center gap-3">
                  <div
                    className="grid h-10 w-10 place-items-center rounded-full"
                    style={{ backgroundColor: `${conta.cor}1f` }}
                  >
                    {conta.tipo === 'CAIXA' ? (
                      <PiggyBank className="w-5 h-5" style={{ color: conta.cor }} />
                    ) : (
                      <CreditCard className="w-5 h-5" style={{ color: conta.cor }} />
                    )}
                  </div>
                  <div>
                    <p className="font-semibold text-slate-800">{conta.nome}</p>
                    <p className="text-xs text-slate-400">{conta.tipo}</p>
                  </div>
                </div>
                <p className={`font-bold tabular-nums ${conta.saldoAtual >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                  {formatCurrency(conta.saldoAtual)}
                </p>
              </div>
            ))}
            {contas.length === 0 && (
              <div className="py-6 text-center">
                <p className="text-sm text-slate-400">Nenhuma conta cadastrada</p>
                <Link to="/financeiro/contas/nova" className="mt-1 inline-block text-sm font-semibold text-primary-600 hover:text-primary-700">
                  Adicionar conta
                </Link>
              </div>
            )}
          </div>
        </Panel>

        {/* Contas a Vencer */}
        <Panel
          title="Próximos Vencimentos (7 dias)"
          icon={<Calendar className="h-4 w-4" />}
          className="lg:col-span-2"
        >
          <div className="space-y-1">
            {contasVencer.slice(0, 8).map((lancamento) => (
              <Link
                key={lancamento.id}
                to={`/financeiro/lancamentos/${lancamento.id}`}
                className="flex items-center justify-between rounded-xl p-3 transition-colors hover:bg-slate-50"
              >
                <div className="flex items-center gap-3">
                  <span className={`h-2 w-2 shrink-0 rounded-full ${lancamento.tipo === 'RECEITA' ? 'bg-emerald-500' : 'bg-rose-500'}`} />
                  <div>
                    <p className="font-semibold text-slate-800">{lancamento.descricao}</p>
                    <div className="flex items-center gap-1.5 text-xs text-slate-400">
                      <Calendar className="w-3 h-3" />
                      {formatDate(lancamento.dataVencimento)}
                      {lancamento.pessoaNome && <span>· {lancamento.pessoaNome}</span>}
                    </div>
                  </div>
                </div>
                <p className={`font-bold tabular-nums ${lancamento.tipo === 'RECEITA' ? 'text-emerald-600' : 'text-rose-600'}`}>
                  {lancamento.tipo === 'RECEITA' ? '+' : '-'} {formatCurrency(lancamento.valor)}
                </p>
              </Link>
            ))}
            {contasVencer.length === 0 && (
              <EmptyState>Nenhuma conta a vencer nos próximos 7 dias</EmptyState>
            )}
          </div>
        </Panel>
      </div>
    </div>
  );
}
